import { createSelector } from 'reselect';
import {
  getCompanyFutures,
  getFormatting,
  getSummary,
  getAnalysis,
} from '@/pages/CompanyReport/redux/selectors';
import { toPercentage } from '@/utilities/formatting';
import { getAveragesSelector } from '@ducks/averages/selectors';
import transformToTimeSeries, {
  seriesArraySortDesending,
} from '@/pages/CompanyReport/utils/tranformToTimeSeries';
import type { Future } from './interfaces';
import { minBy, maxBy } from 'lodash';
import { getViewMode } from '@/redux/location/selectors';

interface KeyMetric {
  value: string;
  title: string;
}

const getFutureKeyMetic = (
  earningsGrowthAnnual: number | null,
  revenueGrowthAnnual: number | null,
  industryName: string,
  netIncomeGrowthAnnualIndustry: number
): KeyMetric => {
  let value = '';
  let title = '';
  if (earningsGrowthAnnual) {
    value = toPercentage(earningsGrowthAnnual, 1);
    title = 'Forecasted annual earnings growth';
  } else if (revenueGrowthAnnual) {
    value = toPercentage(revenueGrowthAnnual, 1);
    title = 'Forecasted annual revenue growth';
  } else if (netIncomeGrowthAnnualIndustry) {
    value = toPercentage(netIncomeGrowthAnnualIndustry, 1);
    title = `Forecasted ${industryName} industry annual growth in earnings`;
  }
  return {
    value,
    title,
  };
};

const formatter = (value: number | null) =>
  value !== null ? toPercentage(value, 1) : 'n/a';

export const getFutureSelector = createSelector(
  [getSummary, getCompanyFutures, getAveragesSelector, getFormatting],
  (summary, future, averages, formatting): Future => {
    const futureScore = summary.score.future;

    const { getIndustryAverage, getMarketAverage } = averages;

    const futureKeyMetics = getFutureKeyMetic(
      future.earningsGrowthAnnual,
      future.revenueGrowthAnnual,
      summary.industryData.primaryName,
      getIndustryAverage('netIncomeGrowthAnnual').value
    );

    const earningsGrowthAnnual = future.earningsGrowthAnnual;
    const revenueGrowthAnnual = future.revenueGrowthAnnual;
    const netIncomeGrowthAnnualIndustry = getIndustryAverage(
      'netIncomeGrowthAnnual'
    ).value;
    const revenueGrowthAnnualIndustry = getIndustryAverage(
      'revenueGrowthAnnual'
    ).value;
    const netIncomeGrowthAnnualMarket = getMarketAverage(
      'netIncomeGrowthAnnual'
    ).value;
    const revenueGrowthAnnualMarket = getMarketAverage(
      'revenueGrowthAnnual'
    ).value;

    const earningsGrowthAnnualFormatted = formatter(earningsGrowthAnnual);
    const netIncomeGrowthAnnualIndustryFormatted = formatter(
      netIncomeGrowthAnnualIndustry
    );
    const netIncomeGrowthAnnualMarketFormatted = formatter(
      netIncomeGrowthAnnualMarket
    );

    const revenueGrowthAnnualFormatted = formatter(revenueGrowthAnnual);
    const revenueGrowthAnnualIndustryFormatted = formatter(
      revenueGrowthAnnualIndustry
    );
    const revenueGrowthAnnualMarketFormatted = formatter(
      revenueGrowthAnnualMarket
    );

    /**
     * Earnings and Revenue Growth Forecasts
     */
    const revenue = [
      ...future.pastRevenueActuals.filter(
        (n) => n[0] >= future.pastRevenueActualsMin
      ),
      ...future.futureRevenueEstimate.filter(
        (n) => n[0] > future.pastRevenueActualsMax
      ),
    ];
    const earnings = [
      ...future.pastNetIncome.filter(
        (n) => n[0] >= future.pastRevenueActualsMin
      ),
      ...future.futureNetIncome.filter(
        (n) => n[0] > future.pastRevenueActualsMax
      ),
    ];
    const freeCashflow = [
      ...future.pastFreeCashFlow.filter(
        (n) => n[0] >= future.pastRevenueActualsMin
      ),
      ...future.futureFreeCashFlow.filter(
        (n) => n[0] > future.pastRevenueActualsMax
      ),
    ];

    const cashflow = [
      ...future.pastCashFlow.filter(
        (n) => n[0] >= future.pastRevenueActualsMin
      ),
      ...future.futureCashFlow.filter(
        (n) => n[0] > future.pastRevenueActualsMax
      ),
    ];

    const revenueMin = minBy(revenue, (n) => n[1]);
    const revenueMax = maxBy(revenue, (n) => n[1]);

    const earningsMin = minBy(earnings, (n) => n[1]);
    const earningsMax = maxBy(earnings, (n) => n[1]);

    const freeCashflowMin = minBy(freeCashflow, (n) => n[1]);
    const freeCashflowMax = maxBy(freeCashflow, (n) => n[1]);

    const cashflowMin = minBy(cashflow, (n) => n[1]);
    const cashflowMax = maxBy(cashflow, (n) => n[1]);

    const dayZero = future.pastRevenueActuals.length
      ? future.pastRevenueActuals[future.pastRevenueActuals.length - 1][0]
      : new Date().getTime();

    /**
     * Earnings per Share Growth Forecasts
     */

    const EPSRange = [...future.pastEPSRange, ...future.futureEPSRange];
    const EPSAnalystConfirmation = future.EPSAnalystConfirmation;
    const EPSAnalysts = future.futureEPSAnalyst.reduce(
      (a, b) => ({
        ...a,
        [b[0]]: b[1],
      }),
      {}
    );

    const analystsPastEPSRangeLow = future.pastEPSRange.map((n) => [
      n[0],
      n[1],
    ]);
    const analystsPastEPSRangeHigh = future.pastEPSRange.map((n) => [
      n[0],
      n[2],
    ]);
    const analystsFutureEPSRangeLow = future.futureEPSRange.map((n) => [
      n[0],
      n[1],
    ]);
    const analystsFutureEPSRangeHigh = future.futureEPSRange.map((n) => [
      n[0],
      n[2],
    ]);

    const EPS = [...future.pastEPS, ...future.futureEPS];
    const EPSMin = minBy(EPS, (n) => n[1]);
    const EPSMax = maxBy(EPS, (n) => n[1]);

    const analystsEPSRangeMin = minBy(EPSRange, (n) => n[1]);
    const analystsEPSRangeMax = maxBy(EPSRange, (n) => n[2]);

    const EPSDayZero = future.pastEPS.length
      ? future.pastEPS[future.pastEPS.length - 1][0]
      : new Date().getTime();

    return {
      /** averages */
      industryROE3Y: getIndustryAverage('ROE').value,
      /**
       * Earnings and Revenue Growth Forecasts
       */
      cashflow,
      cashflowMax: cashflowMax ? cashflowMax[1] : 0,
      cashflowMin: cashflowMin ? cashflowMin[1] : 0,
      cashflowAnalystsMap: future.futureCashflowAnalystsMap,
      dayZero,
      earnings,
      earningsMax: earningsMax ? earningsMax[1] : 0,
      earningsMin: earningsMin ? earningsMin[1] : 0,
      earningsAnalystsMap: future.futureNetIncomeAnalystsMap,
      freeCashflow,
      freeCashflowMax: freeCashflowMax ? freeCashflowMax[1] : 0,
      freeCashflowMin: freeCashflowMin ? freeCashflowMin[1] : 0,
      freeCashflowAnalystsMap: future.futureFreeCashflowAnalystsMap,
      revenue,
      revenueMax: revenueMax ? revenueMax[1] : 0,
      revenueMin: revenueMin ? revenueMin[1] : 0,
      revenueAnalystsMap: future.futureRevenueAnalystsMap,
      /**
       * Earnings per Share Growth Forecasts
       */
      EPSMin: EPSMin ? EPSMin[1] : 0,
      EPSMax: EPSMax ? EPSMax[1] : 0,
      analystsPastEPSRangeLow,
      analystsPastEPSRangeHigh,
      analystsFutureEPSRangeLow,
      analystsFutureEPSRangeHigh,
      analystsEPSRangeMin: analystsEPSRangeMin ? analystsEPSRangeMin[1] : 0,
      analystsEPSRangeMax: analystsEPSRangeMax ? analystsEPSRangeMax[2] : 0,
      EPSAnalysts,
      EPSAnalystConfirmation,
      EPSDayZero,
      EPS,
      //
      keyMetric: futureKeyMetics.value,
      keyMetricTitle: futureKeyMetics.title,
      score: futureScore,
      isFund: summary.isFund,
      companyId: summary.companyId,
      companyName: summary.shortName,
      marketName: getIndustryAverage('netIncomeGrowthAnnual').source,
      currencyISO: formatting.reportingCurrencyISO,
      futureROE3Y: future.futureROE3Y,
      analystCount: summary.analystCount,
      uniqueSymbol: summary.uniqueSymbol,
      currencyUnit: formatting.reportingUnit,
      industryName: summary.industryData.secondaryName,
      revenueGrowthAnnual,
      earningsGrowthAnnual,
      revenueGrowthAnnualMarket,
      netIncomeGrowthAnnualMarket,
      revenueGrowthAnnualIndustry,
      netIncomeGrowthAnnualIndustry,
      earningsGrowthAnnualFormatted,
      netIncomeGrowthAnnualIndustryFormatted,
      netIncomeGrowthAnnualMarketFormatted,
      revenueGrowthAnnualFormatted,
      revenueGrowthAnnualIndustryFormatted,
      revenueGrowthAnnualMarketFormatted,
    };
  }
);

export const futureGrowthPropsFromState = createSelector(
  [getSummary, getCompanyFutures, getAveragesSelector, getFormatting],
  (summary, future, getAveragesSelector, formatting) => {
    const { getIndustryAverage, getMarketAverage } = getAveragesSelector;
    const revenueGrowthAnnual = future.revenueGrowthAnnual;
    const revenueGrowthAnnualFormatted = formatter(revenueGrowthAnnual);
    const earningsGrowthAnnual = future.earningsGrowthAnnual;
    const earningsGrowthAnnualFormatted = formatter(earningsGrowthAnnual);
    const revenueGrowthAnnualMarket = getMarketAverage(
      'revenueGrowthAnnual'
    ).value;
    const revenueGrowthAnnualMarketFormatted = formatter(
      revenueGrowthAnnualMarket
    );
    const netIncomeGrowthAnnualMarket = getMarketAverage(
      'netIncomeGrowthAnnual'
    ).value;
    const netIncomeGrowthAnnualMarketFormatted = formatter(
      netIncomeGrowthAnnualMarket
    );
    const revenueGrowthAnnualIndustry = getIndustryAverage(
      'revenueGrowthAnnual'
    ).value;
    const revenueGrowthAnnualIndustryFormatted = formatter(
      revenueGrowthAnnualIndustry
    );
    const netIncomeGrowthAnnualIndustry = getIndustryAverage(
      'netIncomeGrowthAnnual'
    ).value;
    const netIncomeGrowthAnnualIndustryFormatted = formatter(
      netIncomeGrowthAnnualIndustry
    );
    return {
      companyName: summary.shortName,
      currencyISO: formatting.reportingCurrencyISO,
      industryName: summary.industryData.secondaryName,
      marketName: getMarketAverage('netIncomeGrowthAnnual').source,
      revenueGrowthAnnual,
      revenueGrowthAnnualFormatted,
      earningsGrowthAnnual,
      earningsGrowthAnnualFormatted,
      revenueGrowthAnnualMarket,
      revenueGrowthAnnualMarketFormatted,
      netIncomeGrowthAnnualMarket,
      netIncomeGrowthAnnualMarketFormatted,
      revenueGrowthAnnualIndustry,
      revenueGrowthAnnualIndustryFormatted,
      netIncomeGrowthAnnualIndustry,
      netIncomeGrowthAnnualIndustryFormatted,
    };
  }
);

export const earnigsPerShareGrowthPropsFromState = createSelector(
  [getFutureSelector],
  (future) => {
    return {
      EPS: future.EPS,
      EPSMax: future.EPSMax,
      EPSMin: future.EPSMin,
      EPSAnalysts: future.EPSAnalysts,
      EPSAnalystConfirmation: future.EPSAnalystConfirmation,
      analystsEPSRangeMax: future.analystsEPSRangeMax,
      analystsEPSRangeMin: future.analystsEPSRangeMin,
      analystsPastEPSRangeLow: future.analystsPastEPSRangeLow,
      analystsPastEPSRangeHigh: future.analystsPastEPSRangeHigh,
      analystsFutureEPSRangeLow: future.analystsFutureEPSRangeLow,
      analystsFutureEPSRangeHigh: future.analystsFutureEPSRangeHigh,
      currencyISO: future.currencyISO,
      dayZero: future.dayZero,
    };
  }
);

export const futureROEPropsFromState = createSelector(
  [getFutureSelector],
  (future) => {
    return {
      companyROE: future.futureROE3Y,
      companyName: future.companyName,
      industryROE: future.industryROE3Y,
      industryName: future.industryName,
    };
  }
);

export const getFutureEPSRawDataProps = createSelector(
  [getSummary, getAnalysis, getFormatting],
  (summary, analysis, formatting) => {
    const { legacyChartData } = analysis;
    const futureEPS = legacyChartData.futureEPS;
    const futureEPSAnalysts = legacyChartData.futureEPSAnalyst;
    const futureEPSRange = legacyChartData.futureEPSRange;
    const futureEPSHighEstimates = futureEPSRange.map((epsRange) => {
      if (epsRange.length > 2) return [epsRange[0], epsRange[2]];
      else return [epsRange[0], 0];
    });
    const futureEPSLowEstimates = futureEPSRange.map((epsRange) => {
      if (epsRange.length > 1) return [epsRange[0], epsRange[1]];
      else return [epsRange[0], 0];
    });
    const futureEPSEstimates = transformToTimeSeries([
      futureEPS.filter(
        (estimate) => estimate.length > 1 && estimate[0] > Date.now() //Filter past data
      ),
      futureEPSHighEstimates.filter(
        (estimate) => estimate.length > 1 && estimate[0] > Date.now() //Filter past data
      ),
      futureEPSLowEstimates.filter(
        (estimate) => estimate.length > 1 && estimate[0] > Date.now() //Filter past data
      ),
      futureEPSAnalysts.filter(
        (estimate) => estimate.length > 1 && estimate[0] > Date.now() //Filter past data
      ),
    ]);
    const pastEPSData = legacyChartData.pastEPS;
    const pastEPS = transformToTimeSeries([
      pastEPSData.filter(
        (actual) => actual.length > 1 && actual[0] <= Date.now() //Filter future data
      ),
    ]);
    pastEPS.sort(seriesArraySortDesending);
    return {
      uniqueSymbol: summary.uniqueSymbol,
      reportingCurrencyISO: formatting.reportingCurrencyISO,
      lastEarningsUpdate: analysis.lastEarningsUpdateTimeStamp,
      analystCount: summary.analystCount,
      futureEPSEstimates,
      pastEPS,
    };
  }
);

export const getFutureRevenueGrowthRawDataProps = createSelector(
  [getSummary, getAnalysis, getFormatting, getViewMode],
  (summary, analysis, formatting, viewMode) => {
    const { legacyChartData } = analysis;
    const futureRevenueEstimates = legacyChartData.futureRevenueEstimate;
    const futureNetIncome = legacyChartData.futureNetIncome;
    const futureFreeCashFlow = legacyChartData.futureFreeCashFlow;
    const futureCashFlow = legacyChartData.futureCashFlow;
    const futureRevenueAnalysts = legacyChartData.futureRevenueAnalysts;
    const maxPastTimestamp = analysis.pastRevenueActualsMax;

    const filterPastData = (estimate: number[]) =>
      estimate.length > 1 && estimate[0] > maxPastTimestamp;

    const filterFutureData = (actual: number[]) =>
      actual.length > 1 && actual[0] <= maxPastTimestamp;

    const futureEstimates = transformToTimeSeries([
      futureRevenueEstimates.filter(filterPastData),
      futureNetIncome.filter(filterPastData),
      futureFreeCashFlow.filter(filterPastData),
      futureCashFlow.filter(filterPastData),
      futureRevenueAnalysts.filter(filterPastData),
    ]).sort(seriesArraySortDesending);

    const pastRevenue = legacyChartData.pastRevenueActuals;
    const pastFreeCashflow = legacyChartData.pastFreeCashFlow;
    const pastCashflow = legacyChartData.pastCashFlow;
    const pastNetIncome = legacyChartData.pastNetIncome;

    const pastActuals = transformToTimeSeries([
      pastRevenue.filter(filterFutureData),
      pastNetIncome.filter(filterFutureData),
      pastFreeCashflow.filter(filterFutureData),
      pastCashflow.filter(filterFutureData),
      [],
    ]);
    pastActuals.sort(seriesArraySortDesending);

    return {
      uniqueSymbol: summary.uniqueSymbol,
      reportingCurrencyISO: formatting.reportingCurrencyISO,
      reportingUnit: formatting.reportingUnitText,
      futureEstimates,
      pastFinancialData: pastActuals,
      viewMode,
    };
  }
);
